import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '../../design-system/icons';
import { dsColors } from '../../design-system/tokens/colors';
import {
  ONBOARDING_COPY,
  ONBOARDING_SLIDES,
  resolveMedia,
  type OnboardingSlide,
} from './onboardingSlides';
import { ProgressiveImage, SlideVideo } from './slideMedia';
import { useIsDesktop } from './useIsDesktop';
import { useInvitacionReferido } from '../../hooks/useInvitacionReferido';

/**
 * Carrusel de bienvenida (pre-registro). Réplica del frame de Figma "Carosel"
 * (node 148:451) reutilizando exclusivamente tokens del DS v3 (ver ADR-694):
 * colores desde `dsColors`, tipografía como utilidades arbitrarias con peso
 * numérico explícito, radios con tokens.
 *
 * - Mobile: imagen full-bleed con el contenido pinned abajo sobre un gradiente.
 * - Desktop (`lg:`): split en dos columnas (contenido oscuro + imagen), misma
 *   máquina de estados y subcomponentes (ADR-696).
 *
 * Comportamiento (ADR-695): auto-avance tipo stories (el segmento activo rellena
 * 0→100% en `SLIDE_DURATION`, luego avanza y loopea), pausable (hover en desktop,
 * drag, pestaña oculta). Navegación manual por swipe, flechas del teclado y click
 * en cada segmento. Respeta `prefers-reduced-motion` (sin auto-avance).
 */

// Duración del auto-avance por slide (ms).
const SLIDE_DURATION = 6000;

// Colores resueltos desde el DS (import-only). Mapeo 1:1 con las variables del
// frame de Figma.
const C = {
  title: dsColors.neutral[50], //   Text/Inverse strong   #FAFAFA
  desc: dsColors.neutral[300], //   Text/Inverse weak     #D4D4D8
  disclaimer: dsColors.neutral[50],
  promoBg: dsColors.brand[500], //  Background/Fill brand  #1644EB
  promoText: dsColors.neutral[50],
  btnBg: dsColors.neutral[50], //   Background/Fill        #FAFAFA
  btnText: dsColors.neutral[900], // Text/Strong           #18181B
  segTrack: dsColors.neutral[500], // Background/Fill weak  #71717B
  segFill: dsColors.neutral[50],
};

// Gradiente de legibilidad (mobile): transparente arriba → negro abajo, con los
// stops del frame (41% → ~99%).
const MOBILE_SCRIM =
  'linear-gradient(to bottom, rgba(0,0,0,0) 41%, rgba(0,0,0,0.95) 99%)';

// Legibilidad del texto sobre la imagen en desktop: más suave que el de mobile,
// sólo oscurece el pie (donde van título/bajada + progreso, como en la referencia).
const DESKTOP_IMG_SCRIM =
  'linear-gradient(to bottom, rgba(0,0,0,0) 55%, rgba(0,0,0,0.75) 100%)';

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  );
}

export interface OnboardingCarouselProps {
  /** Acción del CTA "Comenzar a crear" (típicamente → registro). */
  onStart: () => void;
  slides?: OnboardingSlide[];
  /** Frena auto-avance y navegación por teclado (p.ej. con un sheet abierto encima). */
  paused?: boolean;
}

export function OnboardingCarousel({
  onStart,
  slides = ONBOARDING_SLIDES,
  paused = false,
}: OnboardingCarouselProps) {
  const count = slides.length;
  const isDesktop = useIsDesktop();

  /* El badge de créditos tiene que decir lo MISMO que el cartel del formulario de
     al lado (ADR-2026-08-27-programa-referidos): quien llega por una invitación
     vería 15 créditos prometidos en un panel y 10 en el otro, con la
     contradicción a diez centímetros dentro de la misma pantalla. Sin invitación
     válida el copy queda exactamente como estaba. */
  const invitacion = useInvitacionReferido();
  /* "al activar tu plan" y no "al registrarte": desde que el programa paga
     contra el PAGO del invitado, prometerlos en el alta era mentir en la
     pantalla donde más caro sale —el amigo se registra por eso, no los ve, y el
     que queda mal es quien lo invitó—.

     Mide 30 caracteres contra los 31 del copy base, a propósito: el badge es un
     pill con `whitespace-nowrap`, así que un texto más largo no envuelve, se
     desborda. Cualquier redacción que lo reemplace tiene que respetar ese
     ancho o hay que sacarle el `nowrap` primero. */
  const copyPromo = invitacion
    ? `${invitacion.creditos} créditos al activar tu plan`
    : ONBOARDING_COPY.promo;
  const [index, setIndex] = useState(0);
  const [progress, setProgress] = useState(0); // 0..1 del slide activo
  const [reduceMotion] = useState(prefersReducedMotion);

  // Pausa temporal (pestaña oculta) — en ref para que el loop de rAF la lea sin
  // re-suscribirse cada frame.
  const pausedRef = useRef(false);
  const elapsedRef = useRef(0);

  // Navegación manual (click/drag/tecla): cambia de slide y **reinicia** el progreso,
  // pero NO detiene el auto-avance — al cambiar `index` el efecto de abajo reinicia el
  // rAF y sigue auto-avanzando desde la nueva imagen.
  const goTo = useCallback(
    (next: number) => {
      setIndex(((next % count) + count) % count);
      elapsedRef.current = 0;
      setProgress(0);
    },
    [count],
  );
  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  // Auto-avance: acumula tiempo mientras no esté pausado (rAF). Se corta sólo con
  // `paused` (prop, p.ej. sheet abierto) o reduced-motion; la navegación manual NO lo
  // detiene (reinicia el progreso al cambiar `index` y sigue).
  useEffect(() => {
    elapsedRef.current = 0;
    setProgress(0);
    if (reduceMotion || paused) return;

    let raf = 0;
    let prevT: number | null = null;
    const loop = (t: number) => {
      if (prevT == null) prevT = t;
      const dt = t - prevT;
      prevT = t;
      if (!pausedRef.current) {
        elapsedRef.current += dt;
        const p = Math.min(elapsedRef.current / SLIDE_DURATION, 1);
        setProgress(p);
        if (p >= 1) {
          setIndex((i) => (i + 1) % count);
          return; // el cambio de `index` re-dispara el efecto
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [index, count, reduceMotion, paused]);

  // Pausa cuando la pestaña se oculta.
  useEffect(() => {
    const onVis = () => {
      pausedRef.current = document.hidden;
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Flechas del teclado (inactivas si el carrusel está pausado por la prop, p.ej.
  // con un sheet abierto encima en mobile). También detienen el auto-avance (`goTo`).
  useEffect(() => {
    if (paused) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, paused]);

  // Interacción con la imagen (click + drag), unificada con Pointer Events → funciona
  // en desktop (mouse) y mobile (touch): click en la mitad izq./der. = atrás/adelante;
  // arrastrar a un lado u otro = atrás/adelante. Todo pasa por `next`/`prev` → `goTo`,
  // que reinicia el progreso del slide (sin detener el auto-avance). El drag usa pointer
  // capture para seguir el gesto.
  const dragRef = useRef<{ x: number; y: number; moved: boolean } | null>(null);

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return; // sólo botón primario
    dragRef.current = { x: e.clientX, y: e.clientY, moved: false };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // pointer no activo (p.ej. eventos sintéticos): seguimos sin capture.
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const s = dragRef.current;
    if (!s) return;
    if (Math.abs(e.clientX - s.x) > 8 || Math.abs(e.clientY - s.y) > 8) s.moved = true;
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const s = dragRef.current;
    dragRef.current = null;
    if (!s) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (s.moved) {
      // Arrastre horizontal que supera el umbral → navega por dirección.
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) next();
        else prev();
      }
    } else {
      // Click sin arrastre → mitad izquierda = atrás, derecha = adelante.
      const rect = e.currentTarget.getBoundingClientRect();
      if (e.clientX - rect.left > rect.width / 2) next();
      else prev();
    }
  };

  const onPointerCancel = () => {
    dragRef.current = null;
  };

  const slide = slides[index];

  return (
    <section
      aria-roledescription="carrusel"
      aria-label="Descubrí Atlas"
      className="pwa-screen relative w-full min-h-[100svh] overflow-hidden bg-black font-sans select-none lg:h-[100svh] lg:min-h-0 lg:bg-white"
    >
      {/* Región viva para lectores de pantalla. */}
      <p className="sr-only" aria-live="polite">
        Pantalla {index + 1} de {count}: {slide.title}
      </p>

      {/* ── Imagen del carrusel ──
          Mobile: full-bleed detrás del contenido. Desktop: confinada a la MITAD
          DERECHA, con margen a los costados y esquinas redondeadas (como la referencia);
          la mitad izquierda queda para el panel de signup fijo. */}
      <div
        // Cursor pointer por `style` (no la clase `.cursor-pointer`): esa clase dispara
        // el scale-down global de ":active" (index.css, "apretón" en desktop) → la imagen
        // se achicaba al clickear (bordes blancos más anchos). Con el cursor inline se
        // mantiene la afordancia sin ese efecto.
        className="absolute inset-0 z-0 overflow-hidden lg:inset-y-[0.8rem] lg:left-[calc(50%+0.8rem)] lg:right-[0.8rem] lg:rounded-[24px]"
        style={{ touchAction: 'pan-y', cursor: 'pointer' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
      >
        {slides.map((slideBase, i) => {
          // Fondo efectivo según el viewport: en desktop (≥lg) los `desktop` overrides
          // pisan al set base (que es el de mobile); lo que no se define, cae al base.
          // ADR-795.
          const s = resolveMedia(slideBase, isDesktop);
          return (
            <div
              key={slideBase.image}
              aria-hidden={i === index ? undefined : true}
              className="absolute inset-0 transition-opacity duration-700 ease-out"
              style={{ opacity: i === index ? 1 : 0 }}
            >
              <ProgressiveImage
                preview={s.preview}
                src={s.image}
                alt={i === index ? s.alt : ''}
                objectPosition={s.imagePosition}
              />
              {/* Slides con fondo en video (ADR-758): el <video> se monta ENCIMA del póster.
                  Con reduced-motion no se monta (queda sólo la imagen). */}
              {s.video && !reduceMotion ? (
                <SlideVideo
                  // `key` con la fuente: al cruzar el breakpoint el rubro puede cambiar de
                  // video (mobile/desktop) y hay que remontar, no reusar el <video>.
                  key={s.video}
                  preview={s.videoPreview ?? s.video}
                  src={s.video}
                  poster={s.image}
                  active={i === index}
                  objectPosition={s.videoPosition}
                />
              ) : null}
            </div>
          );
        })}
        {/* Legibilidad del texto de abajo: mobile (fuerte) / desktop (suave). */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 lg:hidden"
          style={{ background: MOBILE_SCRIM }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 hidden lg:block"
          style={{ background: DESKTOP_IMG_SCRIM }}
        />
      </div>

      {/* ── Contenido (sobre la imagen, anclado abajo) ──
          Título + bajada + progreso. El bloque de promo + CTA + disclaimer es SÓLO
          mobile: en desktop, crear cuenta vive en el panel de signup a la izquierda. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex flex-col lg:inset-y-[0.8rem] lg:left-[calc(50%+0.8rem)] lg:right-[0.8rem] lg:justify-end">
        <div className="flex w-full flex-col gap-[40px] px-5 pb-[calc(env(safe-area-inset-bottom)+28px)] pt-6 lg:gap-6 lg:px-8 lg:pb-8 lg:pt-0">
          {/* Texto + progreso */}
          <div className="flex w-full flex-col gap-[19px]">
            {/* Pill de promo: en DESKTOP va acá, arriba del título (en mobile va abajo, sobre el
                CTA). Es decorativa: el overlay es `pointer-events-none`, el click navega el carrusel. */}
            <div
              className="hidden w-fit items-center gap-2 rounded-full px-3.5 py-1.5 lg:flex"
              style={{ background: C.promoBg }}
            >
              <Icon name="solid/astroid" size={14} className="shrink-0" style={{ color: C.promoText }} />
              <span
                className="whitespace-nowrap text-[14px] font-[600] leading-[19px]"
                style={{ color: C.promoText }}
              >
                {copyPromo}
              </span>
            </div>
            <div key={index} className="flex w-full flex-col gap-2 animate-fade-in">
              <h1
                className="text-[26px] font-[600] leading-[31px] lg:text-[30px] lg:leading-[36px]"
                style={{ color: C.title }}
              >
                {slide.title}
              </h1>
              <p
                className="text-[14px] font-[600] leading-[19px] lg:text-[15px] lg:leading-[22px]"
                style={{ color: C.desc }}
              >
                {slide.description}
              </p>
            </div>

            {/* `pointer-events-auto`: el overlay es click-through (los clicks van a la
                imagen para navegar), pero los segmentos del progreso sí deben responder. */}
            <div className="pointer-events-auto w-fit">
              <ProgressBar
                count={count}
                index={index}
                // Reduced-motion: el segmento actual se muestra lleno (sin animar).
                progress={reduceMotion ? 1 : progress}
                onSelect={goTo}
              />
            </div>
          </div>

          {/* Promo + CTA + disclaimer — sólo mobile (`pointer-events-auto` para el CTA) */}
          <div className="pointer-events-auto flex w-full flex-col items-center gap-[10px] lg:hidden">
            <div className="flex w-full flex-col items-center">
              <div
                className="z-0 -mb-[10px] flex w-full items-center justify-center gap-2 overflow-hidden rounded-t-md pb-4 pt-[6px]"
                style={{ background: C.promoBg }}
              >
                <Icon
                  name="solid/astroid"
                  size={14}
                  className="shrink-0"
                  style={{ color: C.promoText }}
                />
                <span
                  className="whitespace-nowrap text-[14px] font-[600] leading-[19px]"
                  style={{ color: C.promoText }}
                >
                  {copyPromo}
                </span>
              </div>
              <button
                type="button"
                onClick={onStart}
                className="relative z-10 flex w-full items-center justify-center rounded-md p-4 transition-transform active:scale-[0.99]"
                style={{ background: C.btnBg }}
              >
                <span
                  className="text-[16px] font-[700] leading-[21px]"
                  style={{ color: C.btnText }}
                >
                  {ONBOARDING_COPY.cta}
                </span>
              </button>
            </div>
            <p
              className="text-[12px] font-[500] leading-[17px]"
              style={{ color: C.disclaimer }}
            >
              {ONBOARDING_COPY.disclaimer}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

interface ProgressBarProps {
  count: number;
  index: number;
  progress: number; // 0..1 del segmento activo
  onSelect: (i: number) => void;
}

function ProgressBar({ count, index, progress, onSelect }: ProgressBarProps) {
  return (
    <div className="flex w-[200px] items-center gap-3">
      {Array.from({ length: count }).map((_, i) => {
        const fill = i < index ? 100 : i === index ? progress * 100 : 0;
        return (
          <button
            key={i}
            type="button"
            onClick={() => onSelect(i)}
            aria-label={`Ir a la pantalla ${i + 1} de ${count}`}
            aria-current={i === index ? 'true' : undefined}
            className="relative flex h-5 min-w-0 flex-1 items-center"
          >
            <span
              className="h-[6px] w-full overflow-hidden rounded-full"
              style={{ background: C.segTrack }}
            >
              <span
                className="block h-full rounded-full"
                style={{ width: `${fill}%`, background: C.segFill }}
              />
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default OnboardingCarousel;
